import {
  FOC_TOUR_LABEL_PLANNING_P_PREP_TACTICS,
  focFeedbackPopoverHtml,
} from '../focTourFeedback';
import {
  FOC_REQUIRED_DISPOSITION,
  focPlanningPRequirement,
  focPlanningPTourNavVisible,
  type FocPlanningPTourStepDef,
} from './focPlanningPTourTypes';
import { FOC_PLANNING_P_PREP_TACTICS_TOUR_HREF } from './focPlanningPTourRegistry';

export { FOC_PLANNING_P_PREP_TACTICS_TOUR_HREF, focPlanningPTourNavVisible };

/** Phase index 2 = Prepare Tactics; 6 = IAP Prep (org chart doc). */
export const FOC_PLANNING_P_PREP_TACTICS_TOUR_STEPS: FocPlanningPTourStepDef[] = [
  {
    id: 'sectionIntro',
    phaseIndex: 2,
    title: '10.4 Prep for Tactics Workspace',
    description:
      'Prep for Tactics is a dedicated working phase separate from the Tactics Meeting. It prepares draft strategies, tactics, organization, resources, safety analysis, map products, and supporting products for the next operational period. This phase uses form workspaces rather than an in-app IMH agenda list.',
  },
  focPlanningPRequirement({
    id: 'pp006',
    requirementId: 'FOC-PP-006',
    name: 'Prep for Tactics Working Workspace',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 2,
    tour: 'planning-forms',
    description:
      'PRATUS provides a dedicated Prep for Tactics workspace distinct from the Tactics Meeting, with direct access to ICS 234, 215, 215A, and related next-period products.',
  }),
  focPlanningPRequirement({
    id: 'pp006a',
    requirementId: 'FOC-PP-006a',
    name: 'Deliverable Visibility',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 2,
    tour: 'planning-stepper',
    description:
      'The stepper rail displays required deliverables for Prep for Tactics with status indicators and direct links to applicable products so owners can see readiness, blockers, and dependencies at a glance.',
  }),
  focPlanningPRequirement({
    id: 'pp006b',
    requirementId: 'FOC-PP-006b',
    name: 'Required Product Access',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 2,
    tour: 'ics234-card',
    widget: 'workAnalysisMatrix',
    description:
      'The workspace provides direct access to ICS 234, ICS 215, ICS 215A, draft organization, incident roster, Situation Unit Leader COP products, resource/logistics/finance/environmental/safety inputs, and other approved incident-specific products via the form rail and Hub surfaces.',
  }),
  focPlanningPRequirement({
    id: 'pp006c',
    requirementId: 'FOC-PP-006c',
    name: 'Workspace Persistence',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 2,
    tour: 'planning-form-card',
    widget: 'workAnalysisMatrix',
    description:
      'Users can open, minimize, close, and reopen connected working panes without losing product context, work status, or associated incident information. Multi-pane form layouts retain the selected widgets while you work.',
  }),
  focPlanningPRequirement({
    id: 'pp007',
    requirementId: 'FOC-PP-007',
    name: 'Current and Next Operational-Period Separation',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 2,
    tour: 'working-op-indicator',
    description:
      'PRATUS clearly distinguishes active/current operational-period organization, assignments, resources, and products from draft/next operational-period (Working OP) products on Planning P.',
  }),
  focPlanningPRequirement({
    id: 'pp007a',
    requirementId: 'FOC-PP-007a',
    name: 'Persistent Operational-Period Indicator',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 2,
    tour: 'planning-forms',
    widget: 'workAnalysisMatrix',
    description:
      'PRATUS displays the applicable current or next operational period when users create, review, edit, approve, or publish organization, tactic, resource, assignment, safety, or IAP products. Planning P working forms target the Working OP shown in the operational-period indicator.',
  }),
  focPlanningPRequirement({
    id: 'pp007b',
    requirementId: 'FOC-PP-007b',
    name: 'Draft Organization Controls',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    tour: 'planning-form-card',
    widget: 'incidentOrganizationChart',
    description:
      'Draft positions, assignments, and organization changes apply to the selected operational period. Users can identify whether they are viewing an active or draft/next-period organization before making changes (Working OP vs Historical / approved forms).',
  }),
  focPlanningPRequirement({
    id: 'pp008',
    requirementId: 'FOC-PP-008',
    name: 'Incident Organization Model',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    tour: 'planning-forms',
    widget: 'incidentOrganizationChart',
    description:
      'PRATUS uses a common incident-organization data model for the incident roster, organization chart, ICS 203, ICS 207, section workspaces, resource assignments, and related Planning P products.',
  }),
  focPlanningPRequirement({
    id: 'pp008a',
    requirementId: 'FOC-PP-008a',
    name: 'Organization Development',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    description:
      'Authorized users can create and refine incident organization using IMH-based templates, Coast Guard-specific structures, specialty positions, local configuration, and incident-specific custom positions from the Incident Roster and org-chart editors.',
  }),
  focPlanningPRequirement({
    id: 'pp008b',
    requirementId: 'FOC-PP-008b',
    name: 'Organization and Span of Control',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    description:
      'Span-of-control concerns are intended to surface through visible warnings or indicators without preventing valid command judgment. Dedicated span-of-control warnings are not yet implemented in the org chart; organization decisions remain with authorized commanders.',
  }),
  {
    id: 'focFeedback',
    title: 'FOC Feedback Notes',
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_PLANNING_P_PREP_TACTICS),
  },
];

export function focPlanningPPrepTacticsTourStepIds(): string[] {
  return FOC_PLANNING_P_PREP_TACTICS_TOUR_STEPS.map((step) => step.id);
}
